import React, { KeyboardEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionCreators, ApplicationState } from '../../store';
import { Language } from '../../enums';
import { TRANSLATIONS, TABS } from '../../constants';
import { useResized, useScrolledPast, useTranslate } from '../../hooks/common';
import HeaderAction, { HeaderActionProps, HeaderDropdownAction } from './HeaderAction';
import HeaderMegaMenu from './HeaderMegaMenu';
import HeaderTabs from './HeaderTabs';
import Banners from '../common/Banners';
import ContentContainer from '../common/ContentContainer';
import { IconType } from '../common/Icon';
import MenuIcon from '../common/MenuIcon';
import siemensLogo from '../../../assets/images/siemens-logos/siemens-logo-petrol.svg';
import SelectorTagBar from '../application/SelectorTagBar';
import { SectionColor } from '../common/Section';

type LanguageType = 'en' | 'de';

interface HeaderProps {
    title?: string;
}

const Header = ({ title }: HeaderProps) => {
    const dispatch = useDispatch();
    const banners = useSelector((state: ApplicationState) => state.banners.banners);
    const [menuOpen, setMenuOpen] = useState(false);
    const mobileView = useResized(x => x <= 767);
    const scrolledPastTagbar = useScrolledPast(410);
    const scrolledPastTagbarMobile = useScrolledPast(370);
    const scrolled = useScrolledPast();
    const translate = useTranslate();
    const languages = Object.keys(Language).slice(0, Math.ceil(Object.keys(Language).length / 2)).map(x => Number(x));

    const handleSkipButtonKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();

            location.hash = '';
            location.hash = '#main';
        }
    };

    const setLanguage = (language: Language) => {
        dispatch(actionCreators.setLanguage(language));
        window.localStorage.setItem('language', language.toString());
    };
    
    const headerActions: HeaderActionProps[] = [
        {
            dropdownActions: languages.map<HeaderDropdownAction>(x => ({ name: translate(TRANSLATIONS.header.languages[Language[x].toLowerCase() as LanguageType]), onClick: () => setLanguage(x) })),
            iconType: IconType.Language,
            title: translate(TRANSLATIONS.header.language)
        },
        {
            dropdownActions: [{ name: translate(TRANSLATIONS.header.reportProblem), iconType: IconType.Email, onClick: () => window.location.href = 'mailto:apps-help.industry@siemens.com' }],
            iconType: IconType.Support,
            title: translate(TRANSLATIONS.header.support)
        }
    ];

    return (
        <header>
            <div className='main-row-container'>
                <ContentContainer header>
                    <div className='main-row'>
                        <div className='skip-button' tabIndex={0} onKeyDown={handleSkipButtonKeyDown}>
                            {translate(TRANSLATIONS.header.skipToMainContent)}
                        </div>
                        <div className='header-row-container'>
                            <img className='siemens-logo' src={siemensLogo} alt='Siemens Logo' />
                        </div>
                        {title &&
                            <div className='header-row-container'>
                                <div className='title'>{title}</div>
                            </div>
                        }
                        <div className='action-container'>
                            {headerActions.map(x => <HeaderAction key={x.title} dropdownActions={x.dropdownActions} iconType={x.iconType} title={x.title} />)}
                            <div className='header-row-container'>
                                <MenuIcon open={menuOpen} onClick={() => setMenuOpen(x => !x)} />
                            </div>
                        </div>
                    </div>
                </ContentContainer>
            </div>
            <div className={`addtitional-rows-container ${scrolled ? 'hiden' : ''}`}>
                <div className='secondary-row-container'>
                    <ContentContainer header>
                        <div className='secondary-row'>
                            <HeaderTabs hidden={scrolled || mobileView} tabs={TABS} />
                        </div>
                    </ContentContainer>
                </div>
                {(mobileView ? scrolledPastTagbarMobile : scrolledPastTagbar) &&
                    <div className='tag-bar-container'>
                        <ContentContainer>
                            <SelectorTagBar backgroundColor={SectionColor.White} />
                        </ContentContainer>
                    </div>
                }
                <Banners banners={banners} removeBanner={x => dispatch(actionCreators.removeBanner(x))} />
            </div>
            <HeaderMegaMenu navigationItems={TABS.map(x => ({ ...x }))} open={menuOpen} onClose={() => setMenuOpen(false)} />
        </header>
    );
};

export default Header;
